import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { toast } from 'sonner'
import { Button } from '../../components/ui/Button'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { Download } from 'lucide-react'

interface Voucher {
  code: string
  type: string
  uses: number
  totalDiscount: string
  totalRevenue: string
  aov: string
  uniqueCustomers: number
  firstUsedAt: string | null
  lastUsedAt: string | null
}

interface VoucherListResp {
  codes: Voucher[]
}

type SortKey = 'uses' | 'totalDiscount' | 'totalRevenue' | 'aov' | 'uniqueCustomers' | 'lastUsedAt'

export function VouchereShopifyPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [sortKey, setSortKey] = useState<SortKey>('uses')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(10)
  const [exporting, setExporting] = useState(false)

  const query = useQuery<VoucherListResp>({
    queryKey: ['shopify-vouchers'],
    queryFn: async () => (await api.get<VoucherListResp>('/shopify/discount-codes')).data,
  })

  const allTypes = useMemo(() => {
    const set = new Set<string>()
    for (const v of query.data?.codes ?? []) set.add(v.type)
    return Array.from(set).sort()
  }, [query.data])

  const filteredSorted = useMemo(() => {
    let rows = query.data?.codes ?? []
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      rows = rows.filter((v) => v.code.toLowerCase().includes(q))
    }
    if (typeFilter) {
      rows = rows.filter((v) => v.type === typeFilter)
    }
    rows = [...rows].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1
      switch (sortKey) {
        case 'uses':
          return (a.uses - b.uses) * dir
        case 'uniqueCustomers':
          return (a.uniqueCustomers - b.uniqueCustomers) * dir
        case 'totalDiscount':
          return (Number(a.totalDiscount) - Number(b.totalDiscount)) * dir
        case 'totalRevenue':
          return (Number(a.totalRevenue) - Number(b.totalRevenue)) * dir
        case 'aov':
          return (Number(a.aov) - Number(b.aov)) * dir
        case 'lastUsedAt':
          return (
            ((a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0) -
              (b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0)) *
            dir
          )
      }
    })
    return rows
  }, [query.data, search, typeFilter, sortKey, sortDir])

  const totals = useMemo(() => {
    const rows = filteredSorted
    return {
      codes: rows.length,
      uses: rows.reduce((s, r) => s + r.uses, 0),
      discount: rows.reduce((s, r) => s + Number(r.totalDiscount), 0),
      revenue: rows.reduce((s, r) => s + Number(r.totalRevenue), 0),
    }
  }, [filteredSorted])

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(k)
      setSortDir('desc')
    }
  }

  const total = filteredSorted.length
  const paginatedRows = filteredSorted.slice(page * pageSize, (page + 1) * pageSize)

  const onExport = async () => {
    try {
      setExporting(true)
      await downloadXlsx('/shopify/exports/vouchers')
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Vouchere Shopify</h1>
        <Button variant="secondary" onClick={onExport} loading={exporting}>
          <Download className="h-4 w-4" /> Export XLSX ({total})
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Kpi label="Coduri" value={totals.codes} />
        <Kpi label="Total folosiri" value={totals.uses} />
        <Kpi label="Discount acordat" value={`${totals.discount.toFixed(0)} RON`} />
        <Kpi label="Venit cu voucher" value={`${totals.revenue.toFixed(0)} RON`} />
      </div>

      <Card>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <Field
            label="Caută cod"
            placeholder="ex: WELCOME10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-b4l-text-dim">Tip</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="">Toate</option>
              {allTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                <th className="px-2 py-2">Cod</th>
                <th className="px-2 py-2">Tip</th>
                <Th sortable onClick={() => handleSort('uses')} active={sortKey === 'uses'}>
                  Folosiri
                </Th>
                <Th
                  sortable
                  onClick={() => handleSort('uniqueCustomers')}
                  active={sortKey === 'uniqueCustomers'}
                >
                  Clienți unici
                </Th>
                <Th
                  sortable
                  onClick={() => handleSort('totalDiscount')}
                  active={sortKey === 'totalDiscount'}
                >
                  Discount (RON)
                </Th>
                <Th
                  sortable
                  onClick={() => handleSort('totalRevenue')}
                  active={sortKey === 'totalRevenue'}
                >
                  Venit (RON)
                </Th>
                <Th sortable onClick={() => handleSort('aov')} active={sortKey === 'aov'}>
                  AOV (RON)
                </Th>
                <th className="px-2 py-2 text-right">% disc</th>
                <Th
                  sortable
                  onClick={() => handleSort('lastUsedAt')}
                  active={sortKey === 'lastUsedAt'}
                >
                  Ultima folosire
                </Th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-b4l-text-muted">
                    Se încarcă...
                  </td>
                </tr>
              )}
              {filteredSorted.length === 0 && !query.isLoading && (
                <tr>
                  <td colSpan={9} className="px-2 py-6 text-center text-b4l-text-muted">
                    Niciun voucher găsit
                  </td>
                </tr>
              )}
              {paginatedRows.map((v) => {
                const rev = Number(v.totalRevenue)
                const disc = Number(v.totalDiscount)
                const pctDisc = rev > 0 ? (disc / rev) * 100 : 0
                return (
                  <tr key={v.code} className="border-b border-b4l-border hover:bg-b4l-bg-soft">
                    <td className="px-2 py-2 font-mono">{v.code}</td>
                    <td className="px-2 py-2 text-xs text-b4l-text-muted">{v.type}</td>
                    <td className="px-2 py-2 text-right">{v.uses}</td>
                    <td className="px-2 py-2 text-right">{v.uniqueCustomers}</td>
                    <td className="px-2 py-2 text-right">{disc.toFixed(0)}</td>
                    <td className="px-2 py-2 text-right">{rev.toFixed(0)}</td>
                    <td className="px-2 py-2 text-right">{Number(v.aov).toFixed(0)}</td>
                    <td className="px-2 py-2 text-right text-xs">{pctDisc.toFixed(1)}%</td>
                    <td className="px-2 py-2 text-xs">
                      {v.lastUsedAt ? new Date(v.lastUsedAt).toLocaleDateString('en-GB') : '-'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s)
              setPage(0)
            }}
            itemLabel="vouchere"
          />
        </div>

        <p className="mt-3 text-xs text-b4l-text-muted">
          Notă: se numără doar comenzile vândute (fără anulate, nelivrate și retururi). Venitul e
          valoarea produselor, fără transport. % disc = totalDiscount / totalRevenue (cât din venit
          a fost dat ca discount).
        </p>
      </Card>
    </div>
  )
}

function Th({
  children,
  sortable,
  active,
  onClick,
}: {
  children: React.ReactNode
  sortable?: boolean
  active?: boolean
  onClick?: () => void
}) {
  return (
    <th
      onClick={onClick}
      className={`px-2 py-2 text-right ${sortable ? 'cursor-pointer select-none hover:bg-b4l-bg-soft' : ''} ${
        active ? 'text-b4l-green-dark' : ''
      }`}
    >
      {children}
      {active && <span className="ml-1">↓</span>}
    </th>
  )
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded border border-b4l-border bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-b4l-text-dim">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  )
}
